import type { fabric } from 'fabric';

/*
 * Sharp annotation canvases on high density screens
 *
 * A canvas has as many pixels as its CSS size, then the browser stretches it
 * to screens scaled above 100% (blurring pins, text and icons). Here it gets
 * CSS size × device pixel ratio pixels instead, within a budget: iOS Safari
 * fails on canvases over 4096×4096 pixels. Canvases cover only the visible
 * part of assets (see annotationLayout), so they rarely reach it.
 *
 * Only the pixel density changes: CSS sizes, pointer positions and annotation
 * coordinates stay the same.
 */

const MAX_PIXEL_RATIO = 2;
const MAX_CANVAS_SIDE = 4096;

export const MAX_CANVAS_PIXELS = 4096 * 4096;

/** Largest pixels per CSS pixel a canvas this size can have in the budget */
function getMaxPixelRatio(
  width: number,
  height: number,
  maxPixels = MAX_CANVAS_PIXELS
) {
  if (width <= 0 || height <= 0) return Infinity;
  return Math.min(
    MAX_CANVAS_SIDE / Math.max(width, height),
    Math.sqrt(maxPixels / (width * height))
  );
}

/** Pixels per CSS pixel of a canvas this size, within the budget */
export function getCanvasPixelRatio(
  width: number,
  height: number,
  maxPixels: number,
  devicePixelRatio: number
) {
  const ratio = Math.min(devicePixelRatio, MAX_PIXEL_RATIO);
  if (ratio <= 1 || width <= 0 || height <= 0) return 1;
  const maxRatio = getMaxPixelRatio(width, height, maxPixels);
  return maxRatio > 1 ? Math.min(ratio, maxRatio) : 1;
}

/** Whether a canvas this size is as sharp as the screen allows (in budget) */
export function hasFullCanvasPixelRatio(
  width: number,
  height: number,
  devicePixelRatio: number
) {
  return (
    getMaxPixelRatio(width, height) >=
    Math.min(devicePixelRatio, MAX_PIXEL_RATIO)
  );
}

/**
 * Pixels per CSS pixel of a PDF page rendered by pdf.js. The device's ratio
 * (pdf.js's default), within the budget: pages zoomed far in are rendered
 * below one pixel per CSS pixel (softer) instead of failing on iOS Safari or
 * using a lot of memory.
 */
export function getPdfPagePixelRatio(
  width: number,
  height: number,
  devicePixelRatio: number
) {
  return Math.min(devicePixelRatio, getMaxPixelRatio(width, height));
}

type RetinaScalingCanvas = {
  width: number;
  height: number;
  lowerCanvasEl: HTMLCanvasElement;
  upperCanvasEl?: HTMLCanvasElement;
  contextContainer: CanvasRenderingContext2D;
  contextTop?: CanvasRenderingContext2D;
  __initRetinaScaling: (
    ratio: number,
    canvasEl: HTMLCanvasElement,
    context: CanvasRenderingContext2D
  ) => void;
};

/**
 * Backs the canvas with more pixels on high density screens, recomputed on
 * every resize. Fabric 5 has a single ratio for all canvases
 * (`fabric.devicePixelRatio`): its retina scaling methods are replaced on this
 * canvas instead, with the same behavior but a ratio of its own.
 */
export function enableCanvasPixelRatio(canvas: fabric.Canvas) {
  const target = canvas as fabric.Canvas & {
    getRetinaScaling: () => number;
    _isRetinaScaling: () => boolean;
    _initRetinaScaling: () => void;
  };
  target.enableRetinaScaling = true;
  target.getRetinaScaling = function getRetinaScaling(this: fabric.Canvas) {
    // Turned off by fabric while exporting the canvas
    if (!this.enableRetinaScaling) return 1;
    return getCanvasPixelRatio(
      this.getWidth(),
      this.getHeight(),
      MAX_CANVAS_PIXELS,
      window.devicePixelRatio || 1
    );
  };
  target._isRetinaScaling = function isRetinaScaling() {
    return target.getRetinaScaling() > 1;
  };
  // Unlike fabric's, also sizes the canvases when the ratio is 1: it changes
  // with the canvas size, and the previous one must not be kept
  target._initRetinaScaling = function initRetinaScaling(
    this: RetinaScalingCanvas
  ) {
    const ratio = target.getRetinaScaling();
    this.__initRetinaScaling(ratio, this.lowerCanvasEl, this.contextContainer);
    if (this.upperCanvasEl && this.contextTop) {
      this.__initRetinaScaling(ratio, this.upperCanvasEl, this.contextTop);
    }
  };
  canvas.setDimensions({
    width: canvas.getWidth(),
    height: canvas.getHeight()
  });
}
